"use client";

import Link from "next/link";
import LeaderboardDrawer from "@/components/LeaderboardDrawer";
import TokenBalance from "@/components/TokenBalance";
import { useAuth } from "@/hooks/useAuth";

interface ModeSelectProps {
  isGuest: boolean;
  // The state of the saved run:
  //   "active"    → mid-wheel; primary action is "Continue Run" and a new
  //                 friend match is locked so progress can't be discarded.
  //   "completed" → finished; primary action is "View Stats" and Play vs
  //                 Friend is unlocked (a finished run doesn't block anything).
  //   "none"      → fresh: choose Solo or Play vs Friend.
  runStatus: "none" | "active" | "completed";
  selfName: string;
  onContinue: () => void;
  onSolo: () => void;
  onPlayFriend: () => void;
  onTryDemo: () => void;
}

// The entry screen shown before the first wheel: a purple "Hero or a Villain"
// disc pushed to the left and the play options on the right.
export default function ModeSelect({
  isGuest,
  runStatus,
  selfName,
  onContinue,
  onSolo,
  onPlayFriend,
  onTryDemo,
}: ModeSelectProps) {
  const { user, profile, signOut } = useAuth();

  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-12 px-6 pb-28 pt-10 md:flex-row md:justify-center md:gap-10 lg:gap-16">
      {/* Account controls. Pinned top-left so they never collide with the
          leaderboard drawer tabbed to the right edge. */}
      <div className="fixed left-4 top-4 z-40 flex gap-2">
        <TokenBalance panelSide="left" />
        <Link
          href={user ? "/profile" : "/login"}
          className="rounded-full border border-white/20 bg-background/80 px-4 py-2 text-xs font-bold uppercase tracking-widest text-white/70 backdrop-blur transition hover:border-accent hover:text-accent"
        >
          {user ? profile?.username ?? "Profile" : "Sign In"}
        </Link>
        {user && (
          <button
            onClick={() => signOut()}
            className="rounded-full border border-white/20 bg-background/80 px-4 py-2 text-xs font-bold uppercase tracking-widest text-white/70 backdrop-blur transition hover:border-red-400 hover:text-red-400"
          >
            Sign Out
          </button>
        )}
      </div>

      <LeaderboardDrawer />
      {/* Left: a purple disc with the title */}
      <div className="flex shrink-0 justify-center md:justify-start">
        <div className="flex aspect-square w-[min(82vw,58vh,34rem)] items-center justify-center rounded-full border-4 border-[#1a1a22] bg-gradient-to-b from-accent to-[#5b21b6] p-10 text-center shadow-[0_0_60px_rgba(124,58,237,0.45),inset_0_2px_10px_rgba(255,255,255,0.25)] md:w-[min(44vw,72vh,36rem)]">
          <span
            className="text-[clamp(1.5rem,4.5vw,2.75rem)] font-black uppercase leading-tight tracking-tight text-white"
            style={{
              textShadow:
                "-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000, 0 2px 4px rgba(0,0,0,0.6)",
            }}
          >
            Hero or a Villain
          </span>
        </div>
      </div>

      {/* Right: choose how to play */}
      <div className="flex w-full max-w-sm shrink-0 flex-col gap-4">
        <div className="text-center md:text-left">
          <h2 className="text-[clamp(1.1rem,2.5vw,1.5rem)] font-bold uppercase tracking-widest text-white/80">
            {runStatus === "none" ? "How do you want to play?" : "Welcome back"}
          </h2>
          <p className="mt-1 text-xs uppercase tracking-widest text-white/40">
            Playing as <span className="text-accent">{selfName}</span>
          </p>
        </div>

        {runStatus === "active" ? (
          <>
            <button
              onClick={onContinue}
              className="flex flex-col items-start gap-1 rounded-2xl border border-accent bg-accent/20 px-6 py-5 text-left transition hover:bg-accent/30"
            >
              <span className="text-lg font-bold uppercase tracking-widest text-accent">
                ▶ Continue Run
              </span>
              <span className="text-sm text-white/60">
                Pick up exactly where you left off.
              </span>
            </button>

            <button
              onClick={onPlayFriend}
              className="flex flex-col items-start gap-1 rounded-2xl border border-accent/50 bg-accent/10 px-6 py-5 text-left transition hover:border-accent hover:bg-accent/20"
            >
              <span className="text-lg font-bold uppercase tracking-widest text-accent">
                ⚔️ Play Against a Friend
              </span>
              <span className="text-sm text-white/60">
                Your run is saved — it&apos;ll be here when you get back.
              </span>
            </button>
          </>
        ) : runStatus === "completed" ? (
          <>
            <button
              onClick={onContinue}
              className="flex flex-col items-start gap-1 rounded-2xl border border-accent bg-accent/20 px-6 py-5 text-left transition hover:bg-accent/30"
            >
              <span className="text-lg font-bold uppercase tracking-widest text-accent">
                📊 View Stats
              </span>
              <span className="text-sm text-white/60">
                See how your last run turned out.
              </span>
            </button>

            <button
              onClick={onPlayFriend}
              className="flex flex-col items-start gap-1 rounded-2xl border border-accent/50 bg-accent/10 px-6 py-5 text-left transition hover:border-accent hover:bg-accent/20"
            >
              <span className="text-lg font-bold uppercase tracking-widest text-accent">
                ⚔️ Play Against a Friend
              </span>
              <span className="text-sm text-white/60">
                Get a link to share. Spin side by side and compare final scores.
              </span>
            </button>
          </>
        ) : (
          <>
            <button
              onClick={onPlayFriend}
              className="flex flex-col items-start gap-1 rounded-2xl border border-accent/50 bg-accent/10 px-6 py-5 text-left transition hover:border-accent hover:bg-accent/20"
            >
              <span className="text-lg font-bold uppercase tracking-widest text-accent">
                ⚔️ Play Against a Friend
              </span>
              <span className="text-sm text-white/60">
                Get a link to share. Spin side by side and compare final scores.
              </span>
            </button>

            <button
              onClick={onSolo}
              className="flex flex-col items-start gap-1 rounded-2xl border border-white/20 bg-white/5 px-6 py-5 text-left transition hover:border-white/40 hover:bg-white/10"
            >
              <span className="text-lg font-bold uppercase tracking-widest text-white/90">
                🎯 Solo
              </span>
              <span className="text-sm text-white/60">
                Play on your own and climb the leaderboard.
              </span>
            </button>
          </>
        )}

        {/* Demo — a clearly secondary action, always available even mid-run. */}
        <button
          onClick={onTryDemo}
          className="rounded-2xl border border-dashed border-white/20 bg-transparent px-6 py-3 text-center text-sm font-bold uppercase tracking-widest text-white/50 transition hover:border-amber-400/50 hover:text-amber-300"
        >
          🎲 Try a Demo Run
          <span className="mt-0.5 block text-[0.65rem] font-normal normal-case tracking-normal text-white/40">
            Spin for fun — nothing is saved or submitted.
          </span>
        </button>

        {isGuest && (
          <p className="mt-1 text-center text-sm text-white/50 md:text-left">
            Playing as a guest —{" "}
            <Link
              href="/login"
              className="font-semibold text-accent underline-offset-4 hover:underline"
            >
              sign in
            </Link>{" "}
            to save your runs and earn tokens.
          </p>
        )}
      </div>
    </main>
  );
}
